import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Logo from "@/components/common/Logo";
import { useApp } from "@/context/AppContext";
import { useI18n } from "@/i18n/I18nProvider";
import * as auth from "@/api/authClient";

// Real authentication against WordPress (KHA Auth mu-plugin):
// registration creates a real WP customer, login validates the real password
// (WP core wp_hash_password), sessions use signed JWTs. Google sign-in is
// real OAuth — until the owner configures KHA_GOOGLE_CLIENT_ID/SECRET in
// wp-config.php, the button shows a clear "not configured" note instead of
// pretending to work.

export default function AuthPage() {
  const { t, lang } = useI18n();
  const { login, register: registerUser, user } = useApp();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const [mode, setMode] = useState(params.get("mode") === "reset" ? "reset" : "login");
  const [form, setForm] = useState({ name: "", email: params.get("login") || "", password: "" });
  const [resetKey] = useState(params.get("key") || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [googleCfg, setGoogleCfg] = useState(null); // null = unknown

  useEffect(() => {
    // Google OAuth callback returns ?google=success&token=...
    if (params.get("google") === "success" && params.get("token")) {
      const tok = params.get("token");
      auth.setToken(tok);
      auth.me().then((d) => { login(d); navigate("/account"); }).catch(() => setError(t("auth_failed_generic")));
      return;
    }
    if (params.get("google") && params.get("google") !== "success") {
      setError(params.get("google") === "not_configured" ? t("google_not_configured") : t("auth_failed_generic"));
    }
    // Is Google sign-in configured on the backend? (honest, no fake)
    auth.googleUrl().then((d) => setGoogleCfg(d.configured === true ? d : { configured: false })).catch(() => setGoogleCfg({ configured: false }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { if (user && mode !== "reset") navigate("/account"); }, [user]); // eslint-disable-line

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setNotice(""); setBusy(true);
    try {
      if (mode === "login") {
        const d = await auth.login(form.email, form.password);
        login(d);
        navigate("/account");
      } else if (mode === "register") {
        const d = await auth.register(form.name, form.email, form.password);
        login(d);
        navigate("/account");
      } else if (mode === "forgot") {
        const d = await auth.forgotPassword(form.email);
        setNotice(d.message || t("reset_sent"));
      } else if (mode === "reset") {
        await auth.resetPassword(form.email, resetKey, form.password);
        setNotice(t("reset_done"));
        setParams({});
        setMode("login");
      }
    } catch (err) {
      setError(err.message || t("auth_failed_generic"));
    } finally {
      setBusy(false);
    }
  };

  const startGoogle = async () => {
    setError("");
    try {
      const d = await auth.googleUrl();
      if (d.configured && d.url) window.location.href = d.url;
      else setError(d.message || t("google_not_configured"));
    } catch {
      setError(t("google_not_configured"));
    }
  };

  const F = "h-11 w-full rounded-lg border border-border px-3 text-sm outline-none focus:border-brand";
  const TABS = [["login", t("sign_in")], ["register", t("create_account")]];

  return (
    <div className="mx-auto flex max-w-md flex-col items-center px-4 py-14">
      <Logo />
      <div className="mt-8 w-full rounded-2xl border border-border p-6 shadow-soft">
        {mode !== "forgot" && mode !== "reset" && (
          <div className="mb-6 grid grid-cols-2 rounded-full bg-secondary p-1 text-sm font-semibold">
            {TABS.map(([m, label]) => (
              <button key={m} onClick={() => { setMode(m); setError(""); }} className={`rounded-full py-2 ${mode === m ? "bg-background shadow-soft text-ink" : "text-muted-foreground"}`} data-testid={`tab-${m}`}>{label}</button>
            ))}
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
          {mode === "register" && (
            <div>
              <label className="mb-1 block text-sm font-medium">{t("name")}</label>
              <input value={form.name} onChange={set("name")} required className={F} data-testid="auth-name" />
            </div>
          )}
          {mode !== "forgot" || true ? (
            <div>
              <label className="mb-1 block text-sm font-medium">{t("email")}</label>
              <input type="email" required value={form.email} onChange={set("email")} className={F} data-testid="auth-email" />
            </div>
          ) : null}
          {mode !== "forgot" && (
            <div>
              <label className="mb-1 block text-sm font-medium">{t("password")}</label>
              <input type="password" required minLength={8} value={form.password} onChange={set("password")} className={F} data-testid="auth-password" />
              {mode === "login" && (
                <button type="button" onClick={() => { setMode("forgot"); setError(""); }} className="mt-1 text-xs font-medium text-brand hover:underline" data-testid="forgot-link">
                  {t("forgot_password")}
                </button>
              )}
            </div>
          )}
          {mode === "reset" && <p className="rounded-lg bg-secondary/60 p-3 text-xs text-muted-foreground">{t("reset_note")}</p>}

          {error && <p className="rounded-lg bg-destructive/10 p-3 text-xs font-medium text-destructive" data-testid="auth-error">{error}</p>}
          {notice && <p className="rounded-lg bg-success/10 p-3 text-xs font-medium text-success" data-testid="auth-notice">{notice}</p>}

          <button disabled={busy} className="h-11 w-full rounded-full bg-brand text-sm font-bold text-brand-foreground hover:brightness-105 disabled:opacity-60" data-testid="auth-submit">
            {busy ? "…" : mode === "login" ? t("sign_in") : mode === "register" ? t("create_account") : mode === "forgot" ? t("send_reset_link") : t("reset_password_btn")}
          </button>
        </form>

        {(mode === "login" || mode === "register") && (
          <>
            <div className="my-4 flex items-center gap-3 text-xs text-muted-foreground">
              <span className="h-px flex-1 bg-border" />{t("or_continue_with")}<span className="h-px flex-1 bg-border" />
            </div>
            <button onClick={startGoogle} className="flex h-11 w-full items-center justify-center gap-3 rounded-full border border-border bg-background text-sm font-semibold text-ink hover:bg-secondary" data-testid="google-signin">
              <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.3 6.1 29.4 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.6-.4-3.9z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.3 6.1 29.4 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C36.9 39.2 44 34 44 24c0-1.3-.1-2.6-.4-3.9z"/></svg>
              {t("google_sign_in")}
            </button>
            {googleCfg === null ? null : !googleCfg.configured && (
              <p className="mt-2 text-center text-[11px] leading-4 text-muted-foreground">{t("google_not_configured")}</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
